import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserGroupIcon as HandshakeIcon,
  SparklesIcon,
  TrophyIcon,
  BanknotesIcon,
  ChartBarIcon,
  LightBulbIcon,
  FireIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ClockIcon,
  GiftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ShoppingCartIcon,
  TruckIcon,
  CloudIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  PresentationChartLineIcon,
  BoltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import PartnerDetailsModal from '../components/partnerships/PartnerDetailsModal';
import SmartRecommendationsEngine from '../components/partnerships/SmartRecommendationsEngine';
import PartnershipsAnalytics from '../components/partnerships/PartnershipsAnalytics';
import ContractsManager from '../components/partnerships/ContractsManager';

interface Partner {
  id: string;
  name: string;
  nameAr: string;
  logo: string;
  category: 'marketing' | 'software' | 'logistics' | 'finance' | 'communication' | 'cloud';
  description: string;
  descriptionAr: string;
  discountRate: number;
  estimatedSavings: number;
  actualSavings: number;
  usageCount: number;
  rating: number;
  features: string[];
  featuresAr: string[];
  aiRecommendation: {
    score: number;
    reason: string;
    reasonAr: string;
    urgency: 'low' | 'medium' | 'high' | 'critical';
    potentialROI: number;
    implementationTime: string;
    implementationTimeAr: string;
  };
  trends: {
    month: string;
    savings: number;
    usage: number;
  }[];
  status: 'active' | 'recommended' | 'new' | 'trending';
  contractType: 'monthly' | 'annual' | 'usage-based';
  minSpend: number;
  maxDiscount: number;
}

interface SmartInsight {
  id: string;
  type: 'prediction' | 'optimization' | 'alert' | 'opportunity' | 'trend';
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  impact: 'high' | 'medium' | 'low';
  confidence: number;
  estimatedValue: number;
  timeframe: string;
  timeframeAr: string;
  actionRequired: boolean;
  relatedPartners: string[];
  priority: number;
}

const PartnershipsPage: React.FC = () => {
  const { t, direction } = useLanguage();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [insights, setInsights] = useState<SmartInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'recommended' | 'savings' | 'rating' | 'usage'>('recommended');

  // Simulate AI-powered partnerships data
  useEffect(() => {
    const generatePartnersData = (): Partner[] => {
      return [
        {
          id: '1',
          name: 'Google Ads',
          nameAr: 'إعلانات جوجل',
          logo: '🎯',
          category: 'marketing',
          description: 'Advanced digital advertising platform with AI optimization',
          descriptionAr: 'منصة إعلانات رقمية متقدمة مع تحسين بالذكاء الاصطناعي',
          discountRate: 25,
          estimatedSavings: 45000,
          actualSavings: 52000,
          usageCount: 1247,
          rating: 4.8,
          features: ['AI Campaign Optimization', 'Real-time Analytics', 'Multi-platform Reach'],
          featuresAr: ['تحسين الحملات بالذكاء الاصطناعي', 'تحليلات فورية', 'وصول متعدد المنصات'],
          aiRecommendation: {
            score: 96,
            reason: 'Your current ad spend shows 40% inefficiency. Google Ads AI can optimize this.',
            reasonAr: 'إنفاقك الحالي على الإعلانات يُظهر عدم كفاءة بنسبة 40%. ذكاء جوجل يمكنه تحسين هذا.',
            urgency: 'high',
            potentialROI: 340,
            implementationTime: '2-3 days',
            implementationTimeAr: '2-3 أيام'
          },
          trends: [
            { month: 'يناير', savings: 8500, usage: 95 },
            { month: 'فبراير', savings: 12300, usage: 87 },
            { month: 'مارس', savings: 15600, usage: 92 },
            { month: 'أبريل', savings: 18900, usage: 98 }
          ],
          status: 'recommended',
          contractType: 'monthly',
          minSpend: 5000,
          maxDiscount: 30
        },
        {
          id: '2',
          name: 'Microsoft 365',
          nameAr: 'مايكروسوفت 365',
          logo: '💼',
          category: 'software',
          description: 'Complete productivity suite with cloud integration',
          descriptionAr: 'مجموعة إنتاجية كاملة مع تكامل سحابي',
          discountRate: 20,
          estimatedSavings: 28000,
          actualSavings: 31500,
          usageCount: 890,
          rating: 4.6,
          features: ['Cloud Storage', 'Team Collaboration', 'Advanced Security'],
          featuresAr: ['تخزين سحابي', 'تعاون الفريق', 'أمان متقدم'],
          aiRecommendation: {
            score: 89,
            reason: 'Your team productivity can increase by 35% with integrated tools.',
            reasonAr: 'يمكن زيادة إنتاجية فريقك بنسبة 35% مع الأدوات المتكاملة.',
            urgency: 'medium',
            potentialROI: 280,
            implementationTime: '1 week',
            implementationTimeAr: 'أسبوع واحد'
          },
          trends: [
            { month: 'يناير', savings: 6200, usage: 78 },
            { month: 'فبراير', savings: 7800, usage: 82 },
            { month: 'مارس', savings: 8900, usage: 85 },
            { month: 'أبريل', savings: 9600, usage: 89 }
          ],
          status: 'active',
          contractType: 'annual',
          minSpend: 10000,
          maxDiscount: 25
        },
        {
          id: '3',
          name: 'AWS Cloud',
          nameAr: 'أمازون ويب سيرفيسز',
          logo: '☁️',
          category: 'cloud',
          description: 'Comprehensive cloud computing platform',
          descriptionAr: 'منصة حوسبة سحابية شاملة',
          discountRate: 30,
          estimatedSavings: 65000,
          actualSavings: 71200,
          usageCount: 432,
          rating: 4.9,
          features: ['Scalable Infrastructure', 'AI/ML Services', 'Global CDN'],
          featuresAr: ['بنية تحتية قابلة للتوسع', 'خدمات الذكاء الاصطناعي', 'شبكة توصيل عالمية'],
          aiRecommendation: {
            score: 98,
            reason: 'Critical: Your current infrastructure costs 60% more than AWS optimized setup.',
            reasonAr: 'حرج: بنيتك التحتية الحالية تكلف 60% أكثر من إعداد AWS المحسن.',
            urgency: 'critical',
            potentialROI: 450,
            implementationTime: '2-4 weeks',
            implementationTimeAr: '2-4 أسابيع'
          },
          trends: [
            { month: 'يناير', savings: 12000, usage: 89 },
            { month: 'فبراير', savings: 16500, usage: 92 },
            { month: 'مارس', savings: 19800, usage: 95 },
            { month: 'أبريل', savings: 22900, usage: 97 }
          ],
          status: 'new',
          contractType: 'usage-based',
          minSpend: 15000,
          maxDiscount: 35
        }
      ];
    };

    const generateSmartInsights = (): SmartInsight[] => {
      return [
        {
          id: '1',
          type: 'opportunity',
          title: 'Critical Cost Optimization Opportunity',
          titleAr: 'فرصة حرجة لتحسين التكاليف',
          description: 'AI analysis detected 47% potential savings by switching to optimized partner packages',
          descriptionAr: 'اكتشف تحليل الذكاء الاصطناعي توفير محتمل بنسبة 47% بالتحول لباقات الشركاء المحسنة',
          impact: 'high',
          confidence: 94,
          estimatedValue: 156000,
          timeframe: 'Next 3 months',
          timeframeAr: 'الأشهر الثلاثة القادمة',
          actionRequired: true,
          relatedPartners: ['1', '3'],
          priority: 1
        },
        {
          id: '2',
          type: 'prediction',
          title: 'Market Trend Prediction',
          titleAr: 'توقع اتجاهات السوق',
          description: 'Cloud services demand will increase 340% in your sector within 6 months',
          descriptionAr: 'سيزداد الطلب على الخدمات السحابية بنسبة 340% في قطاعك خلال 6 أشهر',
          impact: 'high',
          confidence: 89,
          estimatedValue: 89000,
          timeframe: '6 months',
          timeframeAr: '6 أشهر',
          actionRequired: true,
          relatedPartners: ['3'],
          priority: 2
        },
        {
          id: '3',
          type: 'alert',
          title: 'Contract Renewal Alert',
          titleAr: 'تنبيه تجديد العقد',
          description: 'Microsoft 365 contract expires in 30 days - negotiate better terms now',
          descriptionAr: 'عقد مايكروسوفت 365 ينتهي خلال 30 يوم - تفاوض على شروط أفضل الآن',
          impact: 'medium',
          confidence: 100,
          estimatedValue: 12000,
          timeframe: '30 days',
          timeframeAr: '30 يوم',
          actionRequired: true,
          relatedPartners: ['2'],
          priority: 3
        }
      ];
    };

    setTimeout(() => {
      setPartners(generatePartnersData());
      setInsights(generateSmartInsights());
      setLoading(false);
    }, 1500);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'from-green-500 to-green-600';
      case 'recommended': return 'from-blue-500 to-purple-600';
      case 'new': return 'from-orange-500 to-red-500';
      case 'trending': return 'from-pink-500 to-rose-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const totalSavings = partners.reduce((sum, partner) => sum + partner.actualSavings, 0);

  if (loading) {
    return (
      <DashboardLayout currentPage="partnerships">
        <div className="flex items-center justify-center min-h-screen">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"
          />
          <span className="mr-4 text-lg font-medium text-gray-700 dark:text-gray-300">
            جاري تحليل الشراكات بالذكاء الاصطناعي...
          </span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="partnerships">
      <div className="p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
              <HandshakeIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                الشراكات الذكية
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                شراكات مدعومة بالذكاء الاصطناعي لتحقيق أقصى توفير وعائد
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-lg"
            >
              <div className="text-center">
                <div className="text-2xl font-bold">{formatCurrency(totalSavings)}</div>
                <div className="text-sm opacity-90">إجمالي التوفير</div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Smart Insights Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {insights.map((insight) => (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className={`bg-gradient-to-r ${
                insight.type === 'opportunity' ? 'from-green-500 to-emerald-500' :
                insight.type === 'prediction' ? 'from-blue-500 to-cyan-500' :
                insight.type === 'alert' ? 'from-red-500 to-orange-500' :
                'from-purple-500 to-pink-500'
              } p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    {insight.type === 'opportunity' && <LightBulbIcon className="w-6 h-6" />}
                    {insight.type === 'prediction' && <SparklesIcon className="w-6 h-6" />}
                    {insight.type === 'alert' && <ExclamationTriangleIcon className="w-6 h-6" />}
                    <div>
                      <h3 className="font-bold text-lg">{insight.titleAr}</h3>
                      <p className="text-sm opacity-90">ثقة {insight.confidence}%</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{formatCurrency(insight.estimatedValue)}</div>
                    <div className="text-xs opacity-75">قيمة متوقعة</div>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {insight.descriptionAr}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {insight.timeframeAr}
                  </span>
                  
                  {insight.actionRequired && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      اتخاذ إجراء
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Smart Recommendations Engine */}
        <SmartRecommendationsEngine className="mb-8" />

        {/* Partnerships Analytics */}
        <PartnershipsAnalytics className="mb-8" />

        {/* Contracts Manager */}
        <ContractsManager className="mb-8" />

        {/* Partners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partners.map((partner) => (
            <motion.div
              key={partner.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Partner Header */}
              <div className={`bg-gradient-to-r ${getStatusColor(partner.status)} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    <div className="text-4xl">{partner.logo}</div>
                    <div>
                      <h3 className="font-bold text-xl">{partner.nameAr}</h3>
                      <p className="text-sm opacity-90">{partner.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{partner.aiRecommendation.score}</div>
                    <div className="text-xs opacity-75">نقاط AI</div>
                  </div>
                </div>
              </div>

              {/* Partner Content */}
              <div className="p-6 space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {partner.descriptionAr}
                </p>

                {/* Savings & Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <BanknotesIcon className="w-5 h-5 text-green-600" />
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400">التوفير</div>
                        <div className="text-lg font-bold text-green-600">{formatCurrency(partner.actualSavings)}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <StarIcon className="w-5 h-5 text-blue-600" />
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400">التقييم</div>
                        <div className="text-lg font-bold text-blue-600">{partner.rating}/5</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Recommendation */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3 rtl:space-x-reverse">
                    <SparklesIcon className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-purple-900 dark:text-purple-300 mb-1">
                        توصية الذكاء الاصطناعي
                      </h4>
                      <p className="text-sm text-purple-700 dark:text-purple-400">
                        {partner.aiRecommendation.reasonAr}
                      </p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-purple-600 dark:text-purple-400">
                          عائد متوقع: {partner.aiRecommendation.potentialROI}%
                        </span>
                        <span className="text-xs text-purple-600 dark:text-purple-400">
                          {partner.aiRecommendation.implementationTimeAr}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      خصم {partner.discountRate}%
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {partner.usageCount} مستخدم
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <button 
                      onClick={() => setSelectedPartner(partner)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      عرض التفاصيل
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Partner Details Modal */}
        <PartnerDetailsModal
          partner={selectedPartner}
          isOpen={!!selectedPartner}
          onClose={() => setSelectedPartner(null)}
        />
      </div>
    </DashboardLayout>
  );
};

export default PartnershipsPage;
